import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2,
  Sparkles,
  CheckCircle2,
  XCircle,
  MessageCircle,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

const DISCORD_INVITE = process.env.REACT_APP_DISCORD_INVITE_URL || "https://discord.gg/rGcCp8hHD";
const DISCORD_TIER_KEYS = new Set(["shift_society"]);
const PROGRAM_PATHS = {
  frequency_reset: { path: "/programs/frequency-reset", label: "Open Frequency Reset" },
  timeline_workbook: { path: "/programs/timeline-workbook", label: "Open the Workbook" },
  thirty_day_shift: { path: "/programs/thirty-day-shift", label: "Begin the 30 days" },
  shift_society: { path: "/programs/shift-plus", label: "Enter Shift+" },
  reprogram_reality: { path: "/programs/reprogram-reality", label: "Open Reprogram Your Reality" },
};

export default function Checkout() {
  const { reference } = useParams();
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [discordId, setDiscordId] = useState("");
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get(`/square/orders/${reference}`);
        if (!cancelled) {
          setOrder(r.data);
          if (r.data.discord_user_id) setDiscordId(r.data.discord_user_id);
        }
      } catch (err) {
        if (!cancelled) setNotFound(true);
      }
    };
    load();
    // Poll every 4s while awaiting; every 30s once paid
    const t = setInterval(load, 4000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [reference]);

  const linkDiscord = async () => {
    if (!/^\d{15,25}$/.test(discordId.trim())) {
      toast.error("That doesn't look like a Discord user ID. It should be all digits.");
      return;
    }
    setLinking(true);
    try {
      const r = await api.post(`/square/link-discord/${reference}`, {
        discord_user_id: discordId.trim(),
      });
      toast.success(r.data.message || "Discord linked.");
      const fresh = await api.get(`/square/orders/${reference}`);
      setOrder(fresh.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't link Discord.");
    } finally {
      setLinking(false);
    }
  };

  if (notFound) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center px-5" data-testid="checkout-not-found">
        <div className="text-center max-w-md">
          <XCircle className="h-10 w-10 text-destructive mx-auto mb-4" />
          <div className="eyebrow mb-3 text-destructive">Order not found</div>
          <h1 className="font-serif text-2xl sm:text-3xl mb-4">This checkout link isn't valid.</h1>
          <Link to="/membership">
            <Button className="btn-pill font-mono uppercase text-xs tracking-widest h-12 px-6">
              Return to pricing
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-accent" />
      </div>
    );
  }

  const paid = order.status === "paid";
  const isDiscordTier = DISCORD_TIER_KEYS.has(order.product_key);
  const hasLinkedDiscord = !!order.discord_user_id;

  return (
    <div className="pt-20 sm:pt-24 min-h-screen aurora grain" data-testid="checkout-page">
      <div className="mx-auto max-w-2xl px-5 sm:px-6 py-10 sm:py-16">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {paid ? (
            <>
              <div className="text-center mb-10">
                <CheckCircle2 className="h-12 w-12 text-accent mx-auto mb-6" />
                <div className="eyebrow mb-3 text-accent">Payment confirmed</div>
                <h1 className="font-serif text-[2.25rem] leading-[1.05] sm:text-5xl mb-4">
                  You're in.
                </h1>
                <p className="font-serif italic text-base sm:text-lg text-muted-foreground max-w-md mx-auto">
                  Access details are on their way to{" "}
                  <span className="not-italic font-mono text-foreground">{order.email}</span>.
                </p>
              </div>

              {isDiscordTier && (
                <div className="border border-accent bg-accent/5 p-5 sm:p-6 mb-6" data-testid="checkout-discord-step">
                  <h3 className="font-serif text-lg sm:text-xl mb-3 leading-snug">
                    {hasLinkedDiscord ? "Discord role assigned" : "One more step — link Discord"}
                  </h3>
                  {hasLinkedDiscord ? (
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Your Shift+ role has been granted in the community. Jump in whenever.
                    </p>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                        Join the server, then paste your Discord user ID to get instant Shift+ role.{" "}
                        <a
                          href={DISCORD_INVITE}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-accent hover:underline inline-flex items-center gap-1"
                          data-testid="checkout-join-discord-link"
                        >
                          <MessageCircle className="h-3 w-3" /> Join the server
                        </a>
                      </p>
                      <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
                        <span className="font-mono uppercase tracking-widest text-[10px]">How to find:</span> Discord → Settings → Advanced → Developer Mode ON → right-click your name → Copy User ID.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Input
                          inputMode="numeric"
                          pattern="[0-9]*"
                          placeholder="e.g. 823647123456789012"
                          value={discordId}
                          onChange={(e) => setDiscordId(e.target.value.replace(/\D/g, ""))}
                          className="h-12 font-mono flex-1"
                          data-testid="checkout-discord-id-input"
                        />
                        <Button
                          onClick={linkDiscord}
                          disabled={linking || !discordId}
                          className="btn-pill font-mono uppercase text-xs tracking-widest h-12"
                          data-testid="checkout-link-discord-btn"
                        >
                          {linking ? "Linking..." : "Link Discord"}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {PROGRAM_PATHS[order.product_key] && (
                  <Link to={PROGRAM_PATHS[order.product_key].path} className="block sm:inline-block">
                    <Button className="w-full sm:w-auto btn-pill font-mono uppercase text-xs tracking-widest h-12 px-6" data-testid="checkout-open-program-btn">
                      {PROGRAM_PATHS[order.product_key].label}
                    </Button>
                  </Link>
                )}
                <Link to="/access" className="block sm:inline-block">
                  <Button variant="outline" className="w-full sm:w-auto btn-pill font-mono uppercase text-xs tracking-widest h-12 px-6" data-testid="checkout-access-btn">
                    My access
                  </Button>
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="eyebrow mb-3 text-accent">Awaiting payment</div>
              <h1 className="font-serif text-[2.25rem] leading-[1.05] sm:text-5xl mb-4">
                Complete your ${order.amount_usd} payment
              </h1>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                If you left the checkout, tap below to return. Otherwise this page will update automatically the moment your payment lands.
              </p>

              {order.checkout_url && (
                <a
                  href={order.checkout_url}
                  className="block sm:inline-block mb-6"
                  data-testid="checkout-resume-btn"
                >
                  <Button
                    size="lg"
                    className="w-full sm:w-auto btn-pill font-mono uppercase text-xs tracking-widest h-12 px-6 gap-2"
                  >
                    Continue to secure checkout <ArrowRight className="h-4 w-4" />
                  </Button>
                </a>
              )}

              <div className="border border-border p-5 sm:p-6 mb-6 flex items-center gap-3">
                <Loader2 className="h-4 w-4 animate-spin text-accent shrink-0" />
                <div>
                  <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">
                    Watching for payment...
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Reference <code className="font-mono text-foreground">{order.reference}</code>
                  </div>
                </div>
              </div>

              <div className="border-t border-border pt-6">
                <div className="eyebrow mb-3">Your order</div>
                <dl className="grid grid-cols-[max-content_1fr] gap-y-3 gap-x-6 text-sm">
                  <dt className="text-muted-foreground font-mono uppercase text-[10px] tracking-widest">Item</dt>
                  <dd className="font-serif text-base">{order.product_name}</dd>
                  <dt className="text-muted-foreground font-mono uppercase text-[10px] tracking-widest">Amount</dt>
                  <dd className="font-serif text-base">${order.amount_usd}</dd>
                  <dt className="text-muted-foreground font-mono uppercase text-[10px] tracking-widest">Email</dt>
                  <dd className="font-mono text-xs break-all">{order.email}</dd>
                  <dt className="text-muted-foreground font-mono uppercase text-[10px] tracking-widest">Status</dt>
                  <dd className="font-mono text-xs uppercase tracking-widest text-accent flex items-center gap-2">
                    <Sparkles className="h-3 w-3" /> Awaiting payment
                  </dd>
                </dl>
              </div>
            </>
          )}

          <div className="mt-10 text-center">
            <Link
              to="/membership"
              className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground hover:text-foreground"
            >
              ← Back to pricing
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
